import { useEffect, useRef, useState } from 'react';

// Up/down chevrons that drive the landing page's full-viewport
// scroll-snap. `.lp-root` is the scroll container; each panel is a
// `.lp-panel`. The up arrow is pinned top-centre, the down arrow
// bottom-centre, and each only shows when there's somewhere to go:
//   - first panel: down only
//   - middle panel: up (top) + down (bottom)
//   - last panel:  up only
//
// The active panel is derived from scroll position (which panel's top
// is nearest the container's top) rather than visibility ratios — that
// stays correct mid-snap and never leaves us showing the wrong arrow.

const CONTAINER = '.lp-root';
const PANEL = '.lp-panel';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function panelsIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(PANEL));
}

export function SnapNav(): JSX.Element | null {
  const [active, setActive] = useState(0);
  const [count, setCount] = useState(0);
  const animRef = useRef(0);

  useEffect(() => {
    const container = document.querySelector<HTMLElement>(CONTAINER);
    if (!container) return;
    setCount(panelsIn(container).length);

    let raf = 0;
    const update = (): void => {
      raf = 0;
      const panels = panelsIn(container);
      if (panels.length === 0) return;
      const containerTop = container.getBoundingClientRect().top;
      let best = 0;
      let bestDist = Infinity;
      panels.forEach((p, i) => {
        const dist = Math.abs(p.getBoundingClientRect().top - containerTop);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      setActive(best);
    };

    const onScroll = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const go = (idx: number): void => {
    const container = document.querySelector<HTMLElement>(CONTAINER);
    if (!container) return;
    const target = panelsIn(container)[idx];
    if (!target) return;
    const top =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;

    // Cancel any in-flight animation (rapid clicks).
    if (animRef.current) cancelAnimationFrame(animRef.current);

    const startTop = container.scrollTop;
    const distance = top - startTop;
    if (Math.abs(distance) < 1) return;

    // Suspend two things for the duration of the programmatic scroll:
    //   1. scroll-snap-type: mandatory — otherwise the snap engine
    //      cancels the scroll partway and relaxes to where it stopped.
    //   2. scroll-behavior: smooth — the container sets this (for nav
    //      anchor links), and it makes EVEN a direct `scrollTop = x`
    //      write animate, so our per-frame writes fought each other and
    //      the scroll barely moved. Forcing `auto` makes each frame land
    //      instantly; our easing drives the motion instead.
    // We're already on a snap point when we restore, so there's no jump.
    const restore = (): void => {
      container.style.scrollSnapType = '';
      container.style.scrollBehavior = '';
    };
    container.style.scrollSnapType = 'none';
    container.style.scrollBehavior = 'auto';

    if (prefersReducedMotion()) {
      container.scrollTop = top;
      restore();
      return;
    }

    const duration = 480;
    let startTs = 0;
    const tick = (ts: number): void => {
      if (!startTs) startTs = ts;
      const t = Math.min(1, (ts - startTs) / duration);
      // easeInOutCubic
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      container.scrollTop = startTop + distance * eased;
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        container.scrollTop = top; // land exactly
        restore();
        animRef.current = 0;
      }
    };
    animRef.current = requestAnimationFrame(tick);
  };

  if (count < 2) return null;

  const hasUp = active > 0;
  const hasDown = active < count - 1;

  return (
    <>
      <button
        type="button"
        className="lp-snapnav-btn lp-snapnav-up"
        onClick={() => go(active - 1)}
        aria-label="Previous panel"
        hidden={!hasUp}
      >
        <Chevron dir="up" />
      </button>
      <button
        type="button"
        className="lp-snapnav-btn lp-snapnav-down"
        onClick={() => go(active + 1)}
        aria-label="Next panel"
        hidden={!hasDown}
      >
        <Chevron dir="down" />
      </button>
    </>
  );
}

function Chevron({ dir }: { dir: 'up' | 'down' }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d={dir === 'down' ? 'M5 9 L12 16 L19 9' : 'M5 15 L12 8 L19 15'}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
